import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Autocomplete,
  Button,
  Grid,
  Select,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import DetailsModal from '@/components/DetailsModal';
import expenseTypeQuery from '@/api/queries/expenseType';
import validations from '@/helpers/validation';
import { DatePickerInput } from '@mantine/dates';
import s3Mutations from '@/api/mutitations/s3';
import Modal from '@/components/Modal/Index';
import { YES_NO } from '@/helpers/enums';
import { useDisclosure } from '@mantine/hooks';
import PropTypes from 'prop-types';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';

function EditAgentExpenses(props) {
  const {
    open,
    close,
    opened,
    setKey,
    expense,
    handleAgentUpdateClick,
  } = props;

  const t = prepareTranslate();
  const { useUpload, useDelete } = s3Mutations;
  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const [deleteOpened, { open: deleteOpen, close: deleteClose }] = useDisclosure();

  const { mutateAsync: uploadMutate } = useUpload();
  const { data: expenseTypeData } = expenseTypeQuery.useGetAllExpenseTypes();
  const { mutateAsync: deleteMutate } = useDelete();

  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');
  const [filesToDelete, setFilesToDelete] = useState([]);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      ...expense,
      files: expense?.files || [],
      isRecurring: expense?.isRecurring ? 'yes' : 'no',
      isPaid: expense?.isPaid ? 'yes' : 'no',
      isRecurringActive: expense?.isRecurringActive ? 'yes' : 'no',
      date: expense?.date ? new Date(expense.date) : null,
      payDate: expense?.payDate ? new Date(expense.payDate) : null,
    },
    validate: {
      value: (value) => validations.required(value),
      type: (value) => validations.required(value),
      date: (value, values) => (
        values.isRecurring === 'yes' ? null : validations.required(value)
      ),
    },
  });
  const isRecurring = form.values.isRecurring === 'yes';

  useEffect(() => {
    if (expense) {
      form.setValues({
        ...expense,
        isRecurring: expense.isRecurring ? 'yes' : 'no',
        isPaid: expense.isPaid ? 'yes' : 'no',
        isRecurringActive: expense.isRecurringActive ? 'yes' : 'no',
        date: expense.date ? new Date(expense.date) : null,
        payDate: expense.payDate ? new Date(expense.payDate) : null,
        files: Array.isArray(expense.files) ? expense.files : [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense]);

  const handleDeleteExpense = () => {
    if (!form.values.deletionReason) {
      form.setFieldError('deletionReason', 'שדה חובה');
      return;
    }

    handleExpensesSave({ ...form.values, status: 'archive', archiveDate: new Date() });
    deleteClose();
  };

  const handleExpensesSave = async (values) => {
    setLoading(true);
    let files;
    try {
      filesToDelete.forEach((file) => {
        const { path } = file;
        deleteMutate({ path });
      });

      const newFiles = form.values.files?.filter((file) => !file.path);

      const clonedValues = { ...values };

      if (newFiles?.length > 0) {
        const formData = new FormData();

        newFiles.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          files = await uploadMutate(formData);
          const oldFiles = form.values.files.filter((file) => file.path);
          clonedValues.files = [...files.files, ...oldFiles];
        } catch (error) {
          setResultLabel(t('pages.agentEdit.notification.failure.title'));
          actionResultOpen();
          return;
        }
      }

      clonedValues.isPaid = clonedValues.isPaid === 'yes';
      clonedValues.isRecurring = clonedValues.isRecurring === 'yes';
      clonedValues.isRecurringActive = clonedValues.isRecurringActive === 'yes';

      const result = await handleAgentUpdateClick({
        expense: clonedValues,
      });

      if (!result.success) {
        setResultLabel(t('pages.agencyCommissionsEdit.notification.failure.title'));
        actionResultOpen();
        form.errors = null;
        return;
      }

      setResultLabel(t('pages.agencyCommissionsEdit.notification.success.title'));
      close();
      actionResultOpen();
      setKey((prev) => prev + 1);
    } catch (error) {
      setResultLabel(t('pages.agencyCommissionsEdit.notification.failure.title'));
      actionResultOpen();
      form.errors = null;
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    close();
  };

  const actions = [
    <Button
      key='primary'
      loading={loading}
      onClick={form.onSubmit(handleExpensesSave)}
      w={'7rem'}
    >
      {t('global.update')}
    </Button>,
    <Button
      key='cancel'
      variant='outline'
      onClick={handleCancelClick}
      w={'7rem'}>
      {t('global.cancel')}
    </Button>,
  ];

  const resultActions = [
    <Button
      key='closeButton' onClick={() => actionResultClose()}>
      {t('global.close')}
    </Button>,
  ];

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
    const fileToRemove = form.values.files[index];

    if (fileToRemove?.path) {
      setFilesToDelete((prev) => [...prev, fileToRemove]);
    }
  };

  const handleCancel = () => {
    deleteClose();
    open();
  };

  const deleteActions = [
    <Button key='delete' w={'6rem'} onClick={handleDeleteExpense}>
      {t('global.delete')}
    </Button>,
    <Button key='cancel' w={'6rem'} onClick={handleCancel} variant='outline'>
      {t('global.cancel')}
    </Button>,
  ];

  const handleDelete = () => {
    close();
    deleteOpen();
  };

  const leftActions = [
    <Button
      key='cancel'
      variant='outline'
      onClick={handleDelete}
      w={'7rem'}
    >
      {t('components.editExpenses.delete')}
    </Button>,
  ];

  const handleChangeIsRecurring = (value) => {
    form.setFieldValue('isRecurring', value);
    if (value === 'no') {
      form.setFieldValue('isRecurringActive', null);
      form.setFieldValue('isPaid', 'no');
    } else {
      form.setFieldValue('isPaid', null);
      form.setFieldValue('payDate', null);
      form.setFieldValue('date', null);
      form.setFieldValue('isRecurringActive', 'yes');
    }
  };

  return (
    <>
      <DetailsModal
        actions={actions}
        leftActions={leftActions}
        open={open}
        close={handleCancelClick}
        opened={opened}
        label={t('components.editExpenses.label')}
      >
        <Grid gutter={{ base: 'xl' }}>
          <Grid.Col span={{ base: 3 }}>
            <Autocomplete
              label={t('components.editExpenses.type')}
              data={expenseTypeData}
              required
              limit="10"
              key={form.key('type')}
              {...form.getInputProps('type')}
            />
            {!isRecurring ? (
              <Select
                label={t('components.editExpenses.isPaid')}
                data={YES_NO}
                required
                key={form.key('isPaid')}
                {...form.getInputProps('isPaid')}
              />
            ) : (
              <Select
                label={t('components.editExpenses.isRecurringActive')}
                data={YES_NO}
                key={form.key('isRecurringActive')}
                {...form.getInputProps('isRecurringActive')}
                defaultValue="yes"
              />)}
          </Grid.Col>

          <Grid.Col span={{ base: 3 }}>
            <TextInput
              type='number'
              autoComplete='off'
              label={t('components.editExpenses.sum')}
              key={form.key('sum')}
              maxLength={7}
              required
              {...form.getInputProps('value')}
            />
            {!isRecurring && (
              <DatePickerInput
                key={'payDate'}
                label={t('components.editExpenses.payDate')}
                {...form.getInputProps('payDate')}
              />
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 3 }}>
            {!isRecurring && (
              <DatePickerInput
                required
                key={'date'}
                label={t('components.editExpenses.date')}
                {...form.getInputProps('date')}
              />
            )}
            <TextInput
              key={'comments'}
              label={t('components.editExpenses.comments')}
              {...form.getInputProps('comments')}
            />

          </Grid.Col>

          <Grid.Col span={{ base: 3 }}>
            <Select
              label={t('components.editExpenses.isRecurring')}
              data={YES_NO}
              key={form.key('isRecurring')}
              {...form.getInputProps('isRecurring')}
              defaultValue="no"
              onChange={handleChangeIsRecurring}
            />
          </Grid.Col>
        </Grid>
        {form.values.files?.length > 0 && (
          <EditFiles
            files={form.values.files}
            onNameChange={onFileNameChange}
            onRemove={onDeleteFile}
          />
        )}
        <UploadFilesButton
          onFilesSelected={onFileSelected}
          label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
        />
      </DetailsModal>
      <DetailsModal
        opened={deleteOpened}
        close={handleCancel}
        label={t('components.editExpenses.verifyDeletion')}
        actions={deleteActions}
      >
        <TextInput
          required
          {...form.getInputProps('deletionReason')}
          label={t('components.editExpenses.deletionReason')}
        />
      </DetailsModal>
      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
    </>
  );
}

EditAgentExpenses.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
  expense: PropTypes.obj,
  handleAgentUpdateClick: PropTypes.func,
};

export default EditAgentExpenses;
